import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Clock, Copy, RefreshCw, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";

import { getSentryTriageStats, type SentryTriageRow } from "@/lib/sentry-triage.functions";

export const Route = createFileRoute("/_authenticated/t1dwpanel/sentry")({
  head: () => ({ meta: [{ name: "robots", content: "noindex, nofollow" }] }),
  component: SentryPanel,
});

const SENTRY_ORG = "am-coastal-holdings";
const SENTRY_PROJECT = "warrior-volunteer";

const FIX_ALL_PROMPT = `Work every unresolved Sentry issue in ${SENTRY_ORG}/${SENTRY_PROJECT} until the inbox is empty. Pull everything live from Sentry with the Sentry tools — there is no snapshot, do not assume anything about which issues exist.

Loop, one issue at a time:
1. List unresolved issues live: search_issues(organizationSlug: '${SENTRY_ORG}', projectSlugOrId: '${SENTRY_PROJECT}', query: 'is:unresolved', limit: 100). Repeat until you have them all.
2. For each issue, pull its full detail and latest event with get_sentry_resource / execute_sentry_tool — exception type and message, culprit, release, the in-app stack frames with file and line, tags, and breadcrumbs.
3. Open the real files named in the in-app frames before deciding anything. Never guess or invent a path.
4. Decide: (a) already fixed in current code, (b) not a bug (permission denied, offline/network abort, extension noise, bot 404s, Turnstile, smoke tests), or (c) a real bug still present.
5. If (a) or (b): leave the code alone and close it in Sentry — update_issue(status: "resolved") for already-fixed, update_issue(status: "ignored") for non-bugs. In the reason, record why: what the evidence was, which file/line you checked, and (for already-fixed) which change fixed it.
6. If (c): make the smallest correct code fix, verify it compiles/typechecks, then update_issue(status: "resolved") with a FULL written record in the reason — not a one-liner — so the issue stands alone as documentation later:
   - Root cause: what actually went wrong and why it triggered.
   - Files and functions changed, with the key lines or logic before vs. after.
   - Server-side changes too (server route, migration, config) if any.
   - How it was verified (typecheck/build/test/manual check).
   - Alternatives considered and rejected, and why.
   - Any follow-up or residual risk to watch.
7. Also update the app where the fix requires it (UI copy, server route, migration) — do not stop at the client if the cause is server side.
8. Move to the next issue. Keep going until no unresolved issues remain.
9. When the inbox is empty, kick off the triage cron so the dashboard stats refresh: invoke this app's \`/api/public/sentry-triage\` server route (POST, with the cron secret header) and confirm it completed.

Rules: batch independent reads in parallel, never resolve an issue you did not verify, and if a fix is genuinely not possible say so and leave it unresolved with a note.
Finish with a per-issue summary in chat: issue short id, decision, and what changed (the exhaustive detail lives on the Sentry issue).`;

type Row = SentryTriageRow;

function seenWithinHours(i: Row, hours: number): boolean {
  return !!i.last_seen && Date.now() - Date.parse(i.last_seen) <= hours * 3_600_000;
}

function needsYou(i: Row): boolean {
  if (i.triage_state === "ignored") return false;
  // A safety-path error that fired in the last few hours is never buried,
  // whatever bucket an earlier pass put it in.
  if (i.is_safety_path && seenWithinHours(i, 6) && i.triage_state !== "fixed") return true;
  if (i.classification === "noise" || i.classification === "stale") return false;
  if (i.triage_state === "fixed") return false;
  if (i.snoozed_until && Date.parse(i.snoozed_until) > Date.now()) return false;
  return true;
}

function relative(iso: string | null | undefined) {
  if (!iso) return "never";
  const diff = Date.now() - Date.parse(iso);
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "less than a minute ago";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function Tile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint: string;
  tone?: "alert" | "good";
}) {
  return (
    <div className="rounded border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={`mt-1 text-3xl font-semibold ${
          tone === "good"
            ? "text-emerald-600"
            : tone === "alert" && value > 0
              ? "text-destructive"
              : "text-foreground"
        }`}
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function SentryPanel() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin", "sentry-triage"],
    queryFn: () => getSentryTriageStats(),
    refetchInterval: 60_000,
  });
  const [, setTick] = useState(0);
  const [readAt, setReadAt] = useState(() => new Date().toISOString());

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 15_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (data) setReadAt(new Date().toISOString());
  }, [data]);

  const stats = useMemo(() => {
    const issues = data?.issues ?? [];
    const actionable = issues.filter(needsYou);
    return {
      actionable,
      needsFix: actionable.length,
      critical: actionable.filter((i) => i.classification === "critical").length,
      safetyPath: actionable.filter((i) => i.is_safety_path).length,
      newOrRising: actionable.filter((i) => i.trend === "up" || i.trend === "new").length,
      usersAffected: actionable.reduce((m, i) => Math.max(m, i.user_count ?? 0), 0),
      eventsOnOpen: actionable.reduce((s, i) => s + (i.event_count ?? 0), 0),
      resolved: issues.filter((i) => i.triage_state === "fixed" || i.resolved_in_sentry_at).length,
      autoClosedNoise: issues.filter(
        (i) => i.triage_state === "ignored" || (i.classification === "noise" && !needsYou(i)),
      ).length,
      quiet: issues.filter(
        (i) => !needsYou(i) && i.classification === "stale" && i.triage_state !== "fixed",
      ).length,
    };
  }, [data]);

  const banner =
    stats.critical > 0 || stats.safetyPath > 0
      ? {
          Icon: ShieldAlert,
          iconClass: "text-destructive",
          title: "Needs attention",
          body: "Something critical or on a safety path is open. Copy the fix-all prompt and paste it into a new Lovable chat.",
          className: "border-destructive/30 bg-destructive/5",
        }
      : stats.actionable.length > 0
        ? {
            Icon: AlertTriangle,
            iconClass: "text-amber-500",
            title: "Worth a look",
            body: `${stats.actionable.length} open issue${stats.actionable.length === 1 ? "" : "s"}, copy the fix-all prompt when you have a minute.`,
            className: "border-amber-500/30 bg-amber-500/5",
          }
        : {
            Icon: ShieldCheck,
            iconClass: "text-primary",
            title: "All clear",
            body: "Nothing needs a code change right now.",
            className: "border-primary/30 bg-primary/5",
          };

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(FIX_ALL_PROMPT);
      toast.success("Fix-all prompt copied. Paste it into Lovable chat.");
    } catch {
      toast.error("Could not copy. Select the prompt below and copy it manually.");
    }
  }

  const BannerIcon = banner.Icon;

  return (
    <AppShell title="Sentry dashboard" eyebrow="Admin">
      <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground">
          Warrior Volunteer only. Coffee, glance, one button.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">

          <span
            className={`inline-block h-2 w-2 rounded-full ${
              isFetching ? "animate-pulse bg-primary" : "bg-emerald-500"
            }`}
          />
          <span>Panel read the table {relative(readAt)}.</span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex items-center gap-1 underline underline-offset-2"
          >
            <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
            Refresh now
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 rounded border border-border bg-card p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Clock className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Last cron run</p>
          {data?.lastRun?.ran_at ? (
            <>
              <p className="text-2xl font-bold text-foreground">
                {relative(data.lastRun.ran_at)}
              </p>
              <p className="text-xs text-muted-foreground">
                {new Date(data.lastRun.ran_at).toLocaleString()}
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-foreground">never</p>
              <p className="text-xs text-muted-foreground">
                The hourly triage pass hasn't recorded a run yet.
              </p>
            </>
          )}
        </div>
      </div>

      <div
        className={`flex flex-wrap items-center justify-between gap-4 rounded border p-4 ${banner.className}`}
      >
        <div className="flex items-start gap-3">
          <BannerIcon className={`mt-0.5 h-5 w-5 shrink-0 ${banner.iconClass}`} />
          <div>
            <p className="text-sm font-semibold text-foreground">{banner.title}</p>
            <p className="text-sm text-muted-foreground">{banner.body}</p>
          </div>
        </div>
        <Button type="button" size="lg" onClick={copyPrompt}>
          <Copy className="h-4 w-4" />
          Copy fix-all prompt
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading stats...</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label="Needs a fix"
              value={stats.needsFix}
              tone="alert"
              hint="Open bugs still needing a code change"
            />
            <Tile
              label="Critical"
              value={stats.critical}
              tone="alert"
              hint="Fatal, rising, or many users"
            />
            <Tile
              label="Safety path"
              value={stats.safetyPath}
              tone="alert"
              hint="Claim flow, applications, event signups, notification delivery"
            />
            <Tile
              label="Resolved"
              value={stats.resolved}
              tone="good"
              hint="Fixed and closed in Sentry"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label="New or rising"
              value={stats.newOrRising}
              hint="Getting worse, not better"
            />
            <Tile
              label="Users affected"
              value={stats.usersAffected}
              hint="Worst single open issue"
            />
            <Tile
              label="Events on open bugs"
              value={stats.eventsOnOpen}
              hint="Total reports still unfixed"
            />
            <Tile
              label="Auto-closed as noise"
              value={stats.autoClosedNoise}
              hint={`Ignored automatically · ${stats.quiet} not recurring`}
            />
          </div>
        </>
      )}
      </div>
    </AppShell>
  );
}

